import {
	ORGANIZATION_CAPABILITIES,
	type OrganizationCapability,
} from "@planvera/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Clock, Mail, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/access-requests")({
	component: AccessRequestsPage,
});

type PendingInvite = {
	id: string;
	organizationId: string;
	organizationName: string;
	capabilities: string[];
};

type AccessRequest = {
	id: string;
	email: string;
	name: string;
	organization: string | null;
	reason: string | null;
	status: string;
	createdAt: Date;
	reviewedAt: Date | null;
	reviewedBy: string | null;
	reviewerName?: string | null;
	pendingInvites?: PendingInvite[];
};

type Organization = {
	id: string;
	name: string;
};

type ClientWithAccessRequests = typeof client & {
	admin: typeof client.admin & {
		accessRequests: {
			list: (input: {
				status?: "pending" | "approved" | "rejected";
				limit?: number;
				offset?: number;
			}) => Promise<{
				requests: Array<{
					id: string;
					email: string;
					name: string;
					organization: string | null;
					reason: string | null;
					status: string;
					createdAt: Date;
					reviewedAt: Date | null;
					reviewedBy: string | null;
					reviewerName?: string | null;
					pendingInvites?: PendingInvite[];
				}>;
				total: number;
			}>;
			approve: (input: {
				requestId: string;
				organizationId: string | null;
				capabilities: OrganizationCapability[];
			}) => Promise<{ success: boolean; message: string }>;
			reject: (input: { requestId: string }) => Promise<{ success: boolean }>;
		};
		organizations: {
			listOrganizations: (input: {
				search?: string;
				limit?: number;
				offset?: number;
			}) => Promise<{
				organizations: Organization[];
				total: number;
			}>;
		};
	};
};

const CAPABILITY_PRESETS = {
	member: [] as OrganizationCapability[],
	admin: [...ORGANIZATION_CAPABILITIES] as OrganizationCapability[],
	custom: null,
} as const;

type CapabilityPreset = keyof typeof CAPABILITY_PRESETS;

const CAPABILITY_LABELS: Record<OrganizationCapability, string> = {
	"org.manage_members": "Manage members",
	"org.manage_settings": "Manage settings",
	"org.manage_subscription": "Manage subscription",
	"org.create_project": "Create projects",
	"org.view_all_projects": "View all projects",
	"org.manage_security": "Manage security",
	"org.manage_integrations": "Manage integrations",
	"org.manage_project_team_templates": "Manage team templates",
	"org.manage_work_packages": "Manage work packages",
};

function AccessRequestsPage() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [selectedRequest, setSelectedRequest] = useState<AccessRequest | null>(
		null,
	);
	const [showDetailsPanelOnMobile, setShowDetailsPanelOnMobile] =
		useState(false);
	const [activeTab, setActiveTab] = useState<
		"pending" | "approved" | "rejected"
	>("pending");
	const [showApproveDialog, setShowApproveDialog] = useState(false);
	const [selectedOrganizationId, setSelectedOrganizationId] = useState<
		string | null
	>(null);
	const [selectedPreset, setSelectedPreset] =
		useState<CapabilityPreset>("member");
	const [selectedCapabilities, setSelectedCapabilities] = useState<
		OrganizationCapability[]
	>([]);

	// Debounce search input
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 300);

		return () => clearTimeout(timer);
	}, [search]);

	// Fetch access requests
	const queryKey = [
		"admin",
		"accessRequests",
		"list",
		{ status: activeTab, limit: 50, offset: 0 },
	];

	const { data, isLoading } = useQuery({
		queryKey,
		queryFn: async () => {
			const result = await (
				client as ClientWithAccessRequests
			).admin.accessRequests.list({
				status: activeTab,
				limit: 50,
				offset: 0,
			});
			return result;
		},
		staleTime: 1 * 60 * 1000, // 1 minute
		gcTime: 5 * 60 * 1000, // 5 minutes
	});

	// Fetch organizations for approval dialog
	const { data: organizationsData } = useQuery({
		queryKey: ["admin", "organizations", "list"],
		queryFn: async () => {
			const result = await (
				client as ClientWithAccessRequests
			).admin.organizations.listOrganizations({
				limit: 100,
				offset: 0,
			});
			return result;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});

	// Approve mutation
	const approveMutation = useMutation({
		mutationFn: async (input: {
			requestId: string;
			organizationId: string | null;
			capabilities: OrganizationCapability[];
		}) => {
			return await (
				client as ClientWithAccessRequests
			).admin.accessRequests.approve(input);
		},
		onSuccess: () => {
			toast.success(t("admin:accessRequests.approve.success"));
			queryClient.invalidateQueries({ queryKey: ["admin", "accessRequests"] });
			setSelectedRequest(null);
			setShowApproveDialog(false);
			setSelectedOrganizationId(null);
			setSelectedPreset("member");
			setSelectedCapabilities([]);
		},
		onError: () => {
			toast.error(t("admin:accessRequests.approve.error"));
		},
	});

	// Reject mutation
	const rejectMutation = useMutation({
		mutationFn: async (requestId: string) => {
			return await (
				client as ClientWithAccessRequests
			).admin.accessRequests.reject({ requestId });
		},
		onSuccess: () => {
			toast.success(t("admin:accessRequests.reject.success"));
			queryClient.invalidateQueries({ queryKey: ["admin", "accessRequests"] });
			setSelectedRequest(null);
		},
		onError: () => {
			toast.error(t("admin:accessRequests.reject.error"));
		},
	});

	const handlePresetChange = (preset: string) => {
		const p = preset as CapabilityPreset;
		setSelectedPreset(p);
		if (p === "member") {
			setSelectedCapabilities([]);
		} else if (p === "admin") {
			setSelectedCapabilities([...ORGANIZATION_CAPABILITIES]);
		}
	};

	const handleCapabilityToggle = (capability: OrganizationCapability) => {
		setSelectedCapabilities((prev) =>
			prev.includes(capability)
				? prev.filter((c) => c !== capability)
				: [...prev, capability],
		);
	};

	const getEffectiveCapabilities = (): OrganizationCapability[] => {
		if (selectedPreset === "custom") {
			return selectedCapabilities;
		}
		return CAPABILITY_PRESETS[selectedPreset] ?? [];
	};

	// Filter by search
	const filteredRequests =
		data?.requests.filter((request) => {
			if (!debouncedSearch) return true;
			const searchLower = debouncedSearch.toLowerCase();
			return (
				request.name.toLowerCase().includes(searchLower) ||
				request.email.toLowerCase().includes(searchLower) ||
				request.organization?.toLowerCase().includes(searchLower) ||
				request.reason?.toLowerCase().includes(searchLower)
			);
		}) ?? [];

	const getStatusBadge = (status: string) => {
		switch (status) {
			case "pending":
				return (
					<Badge variant="secondary">
						<Clock className="mr-1 h-3 w-3" />
						{t("admin:accessRequests.status.pending")}
					</Badge>
				);
			case "approved":
				return (
					<Badge variant="default">
						<Check className="mr-1 h-3 w-3" />
						{t("admin:accessRequests.status.approved")}
					</Badge>
				);
			case "rejected":
				return (
					<Badge variant="destructive">
						<X className="mr-1 h-3 w-3" />
						{t("admin:accessRequests.status.rejected")}
					</Badge>
				);
			default:
				return null;
		}
	};

	return (
		<div className="space-y-6">
			<div>
				<h1 className="font-bold text-3xl">
					{t("admin:accessRequests.title")}
				</h1>
				<p className="text-muted-foreground">
					{t("admin:accessRequests.subtitle")}
				</p>
			</div>

			<Tabs
				value={activeTab}
				onValueChange={(v) =>
					setActiveTab(v as "pending" | "approved" | "rejected")
				}
			>
				<TabsList>
					<TabsTrigger value="pending">
						{t("admin:accessRequests.tabs.pending")}
					</TabsTrigger>
					<TabsTrigger value="approved">
						{t("admin:accessRequests.tabs.approved")}
					</TabsTrigger>
					<TabsTrigger value="rejected">
						{t("admin:accessRequests.tabs.rejected")}
					</TabsTrigger>
				</TabsList>

				<TabsContent value={activeTab} className="mt-6">
					<div className="flex h-[calc(100vh-16rem)] gap-4 overflow-hidden rounded-lg border bg-card shadow-sm">
						{/* Main content area */}
						<div
							className={`flex-1 overflow-hidden ${showDetailsPanelOnMobile ? "hidden md:block" : ""}`}
						>
							<div className="border-b p-4">
								<Input
									placeholder={t("admin:accessRequests.search_placeholder")}
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									className="max-w-md"
								/>
							</div>

							<ScrollArea className="h-[calc(100%-5rem)]">
								<div className="p-4">
									{isLoading ? (
										<div className="space-y-3">
											{Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map(
												(id) => (
													<Skeleton key={id} className="h-16 w-full" />
												),
											)}
										</div>
									) : filteredRequests.length > 0 ? (
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>
														{t("admin:accessRequests.name")}
													</TableHead>
													<TableHead>
														{t("admin:accessRequests.email")}
													</TableHead>
													<TableHead>
														{t("admin:accessRequests.organization")}
													</TableHead>
													<TableHead>
														{t(
															"admin:accessRequests.pendingInvites",
															"Pending Invites",
														)}
													</TableHead>
													<TableHead>
														{t("admin:accessRequests.status_label")}
													</TableHead>
													<TableHead>
														{t("admin:accessRequests.created")}
													</TableHead>
													{activeTab !== "pending" && (
														<TableHead>
															{t("admin:accessRequests.reviewed")}
														</TableHead>
													)}
												</TableRow>
											</TableHeader>
											<TableBody>
												{filteredRequests.map((request: AccessRequest) => (
													<TableRow
														key={request.id}
														onClick={() => {
															setSelectedRequest(request);
															setShowDetailsPanelOnMobile(true);
														}}
														className={`cursor-pointer ${selectedRequest?.id === request.id ? "bg-muted" : ""}`}
													>
														<TableCell className="font-medium">
															{request.name}
														</TableCell>
														<TableCell className="font-mono text-sm">
															{request.email}
														</TableCell>
														<TableCell>
															{request.organization || (
																<span className="text-muted-foreground">—</span>
															)}
														</TableCell>
														<TableCell>
															{request.pendingInvites &&
															request.pendingInvites.length > 0 ? (
																<div className="flex flex-wrap gap-1">
																	{request.pendingInvites.map((invite) => (
																		<Badge
																			key={invite.id}
																			variant="outline"
																			className="text-xs"
																		>
																			{invite.organizationName}
																		</Badge>
																	))}
																</div>
															) : (
																<span className="text-muted-foreground">—</span>
															)}
														</TableCell>
														<TableCell>
															{getStatusBadge(request.status)}
														</TableCell>
														<TableCell className="text-muted-foreground text-sm">
															{new Date(request.createdAt).toLocaleDateString()}
														</TableCell>
														{activeTab !== "pending" && (
															<TableCell className="text-muted-foreground text-sm">
																{request.reviewedAt
																	? new Date(
																			request.reviewedAt,
																		).toLocaleDateString()
																	: "—"}
															</TableCell>
														)}
													</TableRow>
												))}
											</TableBody>
										</Table>
									) : (
										<div className="py-12 text-center">
											<p className="text-muted-foreground">
												{t("admin:accessRequests.no_requests_found")}
											</p>
										</div>
									)}
								</div>
							</ScrollArea>

							{data && data.total > 0 && (
								<div className="border-t p-4">
									<p className="text-muted-foreground text-sm">
										{t("admin:accessRequests.showing_requests", {
											count: filteredRequests.length,
											total: data.total,
										})}
									</p>
								</div>
							)}
						</div>

						{/* Sidebar */}
						<div
							className={`w-full border-l bg-muted/20 md:w-96 lg:w-[28rem] ${showDetailsPanelOnMobile ? "block" : "hidden md:block"}`}
						>
							<ScrollArea className="h-full">
								<div className="p-3 md:p-4">
									{selectedRequest ? (
										<>
											<div className="mb-4 flex items-start justify-between">
												<div>
													<h3 className="font-semibold text-lg">
														{selectedRequest.name}
													</h3>
													<p className="text-muted-foreground text-sm">
														{selectedRequest.email}
													</p>
												</div>
												<Button
													variant="ghost"
													size="sm"
													className="h-8 w-8 p-0 md:hidden"
													onClick={() => setShowDetailsPanelOnMobile(false)}
													title="Close"
												>
													<X className="h-4 w-4" />
												</Button>
											</div>

											<Separator />

											<div className="mt-4 space-y-4">
												<div className="flex flex-col gap-1 text-sm">
													<span className="text-muted-foreground">
														{t("admin:accessRequests.details.status")}
													</span>
													<div>{getStatusBadge(selectedRequest.status)}</div>
												</div>

												{selectedRequest.organization && (
													<div className="flex flex-col gap-1 text-sm">
														<span className="text-muted-foreground">
															{t("admin:accessRequests.details.organization")}
														</span>
														<span className="font-medium">
															{selectedRequest.organization}
														</span>
													</div>
												)}

												{selectedRequest.pendingInvites &&
													selectedRequest.pendingInvites.length > 0 && (
														<div className="flex flex-col gap-1 text-sm">
															<span className="text-muted-foreground">
																{t(
																	"admin:accessRequests.details.pendingInvites",
																	"Pending Organization Invites",
																)}
															</span>
															<div className="space-y-2">
																{selectedRequest.pendingInvites.map(
																	(invite) => (
																		<div
																			key={invite.id}
																			className="flex items-center justify-between rounded-md border bg-background p-2"
																		>
																			<span className="font-medium">
																				{invite.organizationName}
																			</span>
																			<Badge variant="secondary">
																				{invite.capabilities.length > 0
																					? t(
																							"admin:accessRequests.permissions_count",
																							"{{count}} permissions",
																							{
																								count:
																									invite.capabilities.length,
																							},
																						)
																					: t(
																							"admin:accessRequests.approve.dialog.presets.member",
																						)}
																			</Badge>
																		</div>
																	),
																)}
															</div>
														</div>
													)}

												<div className="flex flex-col gap-1 text-sm">
													<span className="text-muted-foreground">
														{t("admin:accessRequests.details.reason")}
													</span>
													<p className="whitespace-pre-wrap rounded-md border bg-background p-3 text-sm">
														{selectedRequest.reason || "—"}
													</p>
												</div>

												<div className="flex flex-col gap-1 text-sm">
													<span className="text-muted-foreground">
														{t("admin:accessRequests.details.created")}
													</span>
													<span className="font-medium">
														{new Date(
															selectedRequest.createdAt,
														).toLocaleDateString()}{" "}
														{new Date(
															selectedRequest.createdAt,
														).toLocaleTimeString()}
													</span>
												</div>

												{selectedRequest.reviewedAt && (
													<>
														<div className="flex flex-col gap-1 text-sm">
															<span className="text-muted-foreground">
																{t("admin:accessRequests.details.reviewed")}
															</span>
															<span className="font-medium">
																{new Date(
																	selectedRequest.reviewedAt,
																).toLocaleDateString()}{" "}
																{new Date(
																	selectedRequest.reviewedAt,
																).toLocaleTimeString()}
															</span>
														</div>

														{selectedRequest.reviewerName && (
															<div className="flex flex-col gap-1 text-sm">
																<span className="text-muted-foreground">
																	{t("admin:accessRequests.details.reviewer")}
																</span>
																<span className="font-medium">
																	{selectedRequest.reviewerName}
																</span>
															</div>
														)}
													</>
												)}

												{selectedRequest.status === "pending" && (
													<div className="mt-6 space-y-2">
														{selectedRequest.pendingInvites &&
														selectedRequest.pendingInvites.length > 0 ? (
															<>
																<p className="text-muted-foreground text-sm">
																	{t(
																		"admin:accessRequests.approve.hasPendingInvites",
																		"This user has pending organization invites. Approving will create their account and they can accept the invites after logging in.",
																	)}
																</p>
																<Button
																	onClick={() => {
																		approveMutation.mutate({
																			requestId: selectedRequest.id,
																			organizationId: null,
																			capabilities: [],
																		});
																	}}
																	disabled={
																		approveMutation.isPending ||
																		rejectMutation.isPending
																	}
																	className="w-full"
																>
																	<Check className="mr-2 h-4 w-4" />
																	{t(
																		"admin:accessRequests.approve.createAccount",
																		"Create Account",
																	)}
																</Button>
															</>
														) : (
															<Button
																onClick={() => setShowApproveDialog(true)}
																disabled={
																	approveMutation.isPending ||
																	rejectMutation.isPending
																}
																className="w-full"
															>
																<Check className="mr-2 h-4 w-4" />
																{t("admin:accessRequests.approve.action")}
															</Button>
														)}
														<Button
															onClick={() =>
																rejectMutation.mutate(selectedRequest.id)
															}
															disabled={
																approveMutation.isPending ||
																rejectMutation.isPending
															}
															variant="destructive"
															className="w-full"
														>
															<X className="mr-2 h-4 w-4" />
															{t("admin:accessRequests.reject.action")}
														</Button>
													</div>
												)}
											</div>
										</>
									) : (
										<div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
											<Mail className="mb-4 h-12 w-12" />
											<p className="text-sm">
												{t("admin:accessRequests.details.select_hint")}
											</p>
										</div>
									)}
								</div>
							</ScrollArea>
						</div>
					</div>
				</TabsContent>
			</Tabs>

			{/* Approval Dialog */}
			<AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("admin:accessRequests.approve.dialog.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("admin:accessRequests.approve.dialog.description", {
								name: selectedRequest?.name,
								email: selectedRequest?.email,
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>

					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="organization">
								{t("admin:accessRequests.approve.dialog.organization")}
							</Label>
							<Select
								value={selectedOrganizationId || "none"}
								onValueChange={(value) =>
									setSelectedOrganizationId(value === "none" ? null : value)
								}
							>
								<SelectTrigger id="organization">
									<SelectValue
										placeholder={t(
											"admin:accessRequests.approve.dialog.selectOrganization",
										)}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="none">
										{t("admin:accessRequests.approve.dialog.noOrganization")}
									</SelectItem>
									{organizationsData?.organizations.map((org) => (
										<SelectItem key={org.id} value={org.id}>
											{org.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-2">
							<Label htmlFor="permissions">
								{t("admin:accessRequests.approve.dialog.permissions")}
							</Label>
							<Select value={selectedPreset} onValueChange={handlePresetChange}>
								<SelectTrigger id="permissions">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="member">
										{t("admin:accessRequests.approve.dialog.presets.member")}
									</SelectItem>
									<SelectItem value="admin">
										{t("admin:accessRequests.approve.dialog.presets.admin")}
									</SelectItem>
									<SelectItem value="custom">
										{t("admin:accessRequests.approve.dialog.presets.custom")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{selectedPreset === "custom" && (
							<div className="space-y-3 rounded-md border p-3">
								{ORGANIZATION_CAPABILITIES.map((capability) => (
									<div key={capability} className="flex items-center space-x-2">
										<Checkbox
											id={capability}
											checked={selectedCapabilities.includes(capability)}
											onCheckedChange={() => handleCapabilityToggle(capability)}
										/>
										<Label
											htmlFor={capability}
											className="cursor-pointer font-normal text-sm"
										>
											{CAPABILITY_LABELS[capability]}
										</Label>
									</div>
								))}
							</div>
						)}
					</div>

					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("admin:accessRequests.approve.dialog.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (selectedRequest) {
									approveMutation.mutate({
										requestId: selectedRequest.id,
										organizationId: selectedOrganizationId,
										capabilities: getEffectiveCapabilities(),
									});
								}
							}}
							disabled={approveMutation.isPending || !selectedRequest}
						>
							{approveMutation.isPending
								? t("admin:accessRequests.approve.dialog.approving")
								: t("admin:accessRequests.approve.dialog.approve")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
